import { isNotEmpty } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { get, isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import { Box } from '../../../../../Common/BoxV2';
import { findBellowByType } from '../../../../../DolEditor/helpers/nodeValue';
import { useBlankV2DoTestStore } from '../../../../plugins-common/BlankV2/store/do-test.store';
import { BLANK_V2 } from '../../../../plugins-common/BlankV2/types';
import { CORRECT_ANSWER } from '../../../../plugins-common/CorrectAnswers/types';
import { Nothing } from '../../../../shared-components/Nothing';
import { useExerciseGapFillWithHintDoTestStore } from '../../store/exercise-gap-fill-with-hint-do-test.store';
import { GapFillInlineDropdown } from './GapFillInlineDropdown';
import { useRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';

const Main = styled(Box)`
  .blank-option-input {
    margin-right: 4px;

    .text-question-no,
    .text-missing,
    .text-user-answer {
      font-size: 18px;
      font-weight: 500;
      line-height: 28px;
    }

    .text-missing {
      width: 48px;
    }

    &.is-error {
      border-bottom: 1px solid ${colorsV2.red100};

      .text-question-no,
      .text-missing,
      .text-user-answer {
        color: ${colorsV2.red100};
      }
    }

    &.is-missing {
      border-bottom: 1px solid ${colorsV2.gray40};

      .text-question-no,
      .text-missing,
      .text-user-answer {
        color: ${colorsV2.green100};
      }
    }

    &.is-correct {
      border-bottom: 1px solid ${colorsV2.green80};

      .text-question-no,
      .text-missing,
      .text-user-answer {
        color: ${colorsV2.green100};
      }
    }
  }
`;

export const GapFillWithHintBlankV2Base = observer((props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const store = useBlankV2DoTestStore();
  const rootElement = useRootElement();
  const gapFillStore = useExerciseGapFillWithHintDoTestStore();
  const entries = findBellowByType(rootElement, CORRECT_ANSWER).filter(
    (n) => n.forId === element.id
  );
  // TODO: [DynamicImport] temporary fix to bypass store not initialized when element loaded async
  if (!store || !gapFillStore) {
    return <div {...attributes}>{children}</div>;
  }

  const blanks = findBellowByType(rootElement, BLANK_V2);
  const blankOrder =
    blanks.findIndex((blank) => blank.id === element.id) + (rootElement.startFrom ?? 1);

  const getUserAnswer = () => {
    let answer = '';
    const correctAnswer = get(entries, '0.correctAnswer', '');

    if (gapFillStore.isViewResult) {
      answer = gapFillStore.userAnswers.get(element.id)?.userAnswer || '';
    } else {
      answer = store.userAnswers.get(element.id)?.userAnswer || '';
    }

    const isCorrect =
      isNotEmpty(answer) &&
      correctAnswer
        .split('/')
        .map((ans) => ans.trim().toLowerCase())
        .some((ans) => ans === answer.toLowerCase());
    const isMissing = isEmpty(answer) && !isCorrect;

    return {
      isMissing,
      isCorrect,
      userAnswer: answer || '',
      userAnswerDisplayName: gapFillStore.dropdownOptions.get(answer)?.displayName,
      correctAnswerDisplayName: gapFillStore.dropdownOptions.get(correctAnswer)?.displayName,
    };
  };

  const { isMissing, correctAnswerDisplayName, isCorrect, userAnswer, userAnswerDisplayName } =
    getUserAnswer();

  const onUserAnswer = (value) => {
    store.update({ id: element.id, userAnswer: value });
  };

  const renderResult = React.useCallback(() => {
    if (isMissing) {
      return (
        <span className={cl('blank-option-input', 'is-missing')}>
          <span className="text-question-no">{`${blankOrder}. `}</span>
          <span className="text-user-answer">{correctAnswerDisplayName}</span>
        </span>
      );
    }
    if (isCorrect) {
      return (
        <span className={cl('blank-option-input', 'is-correct')}>
          <span className="text-question-no">{`${blankOrder}. `}</span>
          <span className="text-user-answer">{correctAnswerDisplayName}</span>
        </span>
      );
    }
    return (
      <span className={cl('blank-option-input', 'is-error')}>
        <span className="text-question-no">{`${blankOrder}. `}</span>
        <span className="text-user-answer">{userAnswerDisplayName}</span>
      </span>
    );
  }, [blankOrder, correctAnswerDisplayName, isCorrect, isMissing, userAnswerDisplayName]);

  return (
    <Main
      display="inline"
      contentEditable={false}
      borderRadius="8px"
      data-slate-zero-width
      mx="8px"
      px="2px"
      py="4px"
      onMouseDown={(e) => e.preventDefault()}
      {...attributes}
    >
      <Box onMouseDown={(e) => e.preventDefault()} contentEditable={false} display="inline">
        {gapFillStore.isViewResult ? (
          renderResult()
        ) : (
          <GapFillInlineDropdown
            options={gapFillStore.gapFillOptions}
            value={userAnswer}
            onChange={onUserAnswer}
            blankId={element.id}
          />
        )}
      </Box>
      {children}
    </Main>
  );
});

export const GapFillWithHintBlankV2 = observer((props: RenderElementProps) => {
  const store = useBlankV2DoTestStore();
  const gapFillStore = useExerciseGapFillWithHintDoTestStore();

  if (!store || !gapFillStore) {
    return <Nothing {...props} />;
  }

  return <GapFillWithHintBlankV2Base {...props} />;
});
